import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Download, Loader2, Lock, ShieldCheck, Sparkles, CheckCircle2, XCircle, X, CreditCard } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { trackEvent } from "@/lib/firebase";
import { toast } from "sonner";

/*
 * ── FIREBASE STORAGE SECURITY RULES (for the future Firebase swap) ──────────
 * The PDFs must only be readable by an authenticated user whose Firestore
 * document (/users/{uid}) lists the ebook ID inside `purchasedBooks`:
 *
 *   rules_version = '2';
 *   service firebase.storage {
 *     match /b/{bucket}/o {
 *       match /ebooks/{ebookId}/{fileName} {
 *         allow write: if false; // uploads only via Admin SDK
 *         allow read: if request.auth != null
 *           && firestore.exists(/databases/(default)/documents/users/$(request.auth.uid))
 *           && ebookId in firestore.get(
 *                /databases/(default)/documents/users/$(request.auth.uid)).data.purchasedBooks;
 *       }
 *     }
 *   }
 *
 * Today the same guarantee is enforced server-side: GET /api/ebooks/{id}/download
 * returns the pdfFileUrl only when the JWT user's `purchasedBooks` contains the ID.
 * ────────────────────────────────────────────────────────────────────────────
 */

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const CheckoutModal = ({ book, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(!!book);
  const [phase, setPhase] = useState("review"); // review | processing | success | failed
  const [errorMsg, setErrorMsg] = useState("");

  // Reset internal state when a DIFFERENT book is selected (not on every open toggle).
  useEffect(() => {
    if (book) {
      setOpen(true);
      setPhase("review");
      setErrorMsg("");
    } else {
      setOpen(false);
    }
  }, [book?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // When the user fully closes the dialog (X, ESC, Done), notify the parent
  // so it clears `checkout` and unmounts us next render.
  const handleOpenChange = (v) => {
    if (phase === "processing") return; // don't allow close mid-payment
    setOpen(v);
    if (!v) onClose?.();
  };

  const grant = (id) => {
    trackEvent("purchase", { currency: "INR", value: book.price, items: [{ item_id: id, item_name: book.title }] });
    onSuccess(id);
  };

  const pay = async () => {
    setPhase("processing");
    setErrorMsg("");
    try {
      const { data } = await api.post(`/ebooks/${book.id}/order`);
      if (data.already_owned) {
        setPhase("success");
        grant(book.id);
        return;
      }
      if (data.mode === "live") {
        // 🔧 Close the outer Radix Dialog BEFORE Razorpay opens.
        // Radix's focus-trap + `onInteractOutside` fight with Razorpay's
        // cross-origin iframe — otherwise users can't type into the phone
        // field or dismiss the checkout. Reopen the dialog in each callback
        // so success/failed/processing states still surface.
        setOpen(false);
        await openRazorpayCheckout({
          keyId: data.key_id,
          amount: data.amount,
          currency: data.currency,
          orderId: data.order_id,
          name: "Language Nest India",
          description: book.title,
          prefill: (() => {
            const phone = (user?.phone || "").replace(/\D/g, "").slice(-10);
            const p = { name: user?.name || "", email: user?.email || "" };
            if (phone.length === 10) p.contact = phone;
            return p;
          })(),
          onSuccess: async (res) => {
            setOpen(true);
            setPhase("processing");
            try {
              await api.post(`/ebooks/${book.id}/verify`, {
                order_id: data.order_id,
                payment_id: res.razorpay_payment_id,
                signature: res.razorpay_signature,
              });
              grant(book.id);
              setPhase("success");
              toast.success(`Payment confirmed — "${book.title}" unlocked!`);
            } catch (err) {
              const msg = formatApiErrorDetail(err.response?.data?.detail) || "Payment verification failed";
              setErrorMsg(msg);
              setPhase("failed");
              toast.error(msg);
            }
          },
          onFailure: (info) => {
            setOpen(true);
            const msg = `${info.description} (${info.code})`;
            setErrorMsg(msg);
            setPhase("failed");
            toast.error(`Payment failed: ${msg}`);
          },
          onDismiss: () => {
            // Same UPI-intent rescue used by course checkout
            setOpen(true);
            setPhase("processing");
            toast.info("Checking payment status — please wait a moment…");
            let elapsed = 0;
            const iv = setInterval(async () => {
              elapsed += 3;
              try {
                const { data: st } = await api.get(`/ebooks/orders/${data.order_id}/status`);
                if (st.status === "paid") {
                  clearInterval(iv);
                  grant(book.id);
                  setPhase("success");
                  toast.success(`Payment confirmed — "${book.title}" unlocked!`);
                  return;
                }
                if (st.status === "failed" || st.status === "cancelled") {
                  clearInterval(iv);
                  setErrorMsg("Payment was not completed. No amount will be charged.");
                  setPhase("failed");
                  return;
                }
                if (elapsed >= 30) {
                  clearInterval(iv);
                  setPhase("review");
                  toast.info("Still processing on Razorpay's side — refresh in a minute or contact support if your account was debited.");
                }
              } catch {
                if (elapsed >= 30) { clearInterval(iv); setPhase("review"); }
              }
            }, 3000);
          },
        });
        return;
      }
      // MOCK mode (no Razorpay keys): simulate a 2s payment
      setTimeout(async () => {
        try {
          await api.post(`/ebooks/${book.id}/verify`, { order_id: data.order_id });
          grant(book.id);
          setPhase("success");
        } catch (err) {
          const msg = formatApiErrorDetail(err.response?.data?.detail) || "Payment failed";
          setErrorMsg(msg);
          setPhase("failed");
        }
      }, 2000);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Payment failed";
      setErrorMsg(msg);
      setPhase("failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 rounded-3xl overflow-hidden border-none" data-testid="ebook-checkout-modal">
        <DialogTitle className="sr-only">Checkout</DialogTitle>
        <DialogDescription className="sr-only">Complete your e-book purchase.</DialogDescription>
        {book && (
          <div>
            <div className="bg-brand-red text-white px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-bold text-lg">
                <CreditCard className="w-5 h-5" /> Secure Checkout
              </div>
              {phase !== "processing" && (
                <button onClick={() => handleOpenChange(false)} data-testid="checkout-close-btn" aria-label="Close" className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 grid place-items-center transition">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="p-6">
              <AnimatePresence mode="wait">
                {phase === "review" && (
                  <motion.div key="review" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <div className="flex gap-4">
                      <img src={book.coverImage} alt={book.title} className="w-20 h-28 rounded-xl object-cover ring-1 ring-brand-navy/10 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-brand-navy leading-snug">{book.title}</div>
                        <div className="text-xs text-brand-navy/50 mt-1 line-clamp-2">{book.description}</div>
                        <div className="mt-2 font-heading font-black text-2xl text-brand-red">{inr(book.price)}</div>
                      </div>
                    </div>
                    <div className="mt-5 rounded-2xl bg-brand-muted-bg p-4 text-xs text-brand-navy/60 flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      Instant download after payment. Lifetime access from any device — tied to your Language Nest account.
                    </div>
                    <Button onClick={pay} data-testid="checkout-pay-btn" className="mt-5 w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-bold text-base brand-shadow btn-scale">
                      Pay {inr(book.price)}
                    </Button>
                  </motion.div>
                )}
                {phase === "processing" && (
                  <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-10 grid place-items-center text-center" data-testid="checkout-processing">
                    <Loader2 className="w-10 h-10 animate-spin text-brand-red" />
                    <div className="mt-4 font-semibold text-brand-navy">Processing payment…</div>
                    <div className="text-xs text-brand-navy/50 mt-1">Please don&apos;t close this window</div>
                  </motion.div>
                )}
                {phase === "success" && (
                  <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-10 grid place-items-center text-center" data-testid="checkout-success">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
                      <CheckCircle2 className="w-14 h-14 text-emerald-500" />
                    </motion.div>
                    <div className="mt-3 font-heading font-bold text-xl text-brand-navy">Payment successful!</div>
                    <div className="text-xs text-brand-navy/50 mt-1">Your e-book is ready to download</div>
                    <Button onClick={() => handleOpenChange(false)} data-testid="checkout-success-close-btn" className="mt-5 rounded-full bg-brand-red hover:bg-brand-red-hover">Done</Button>
                  </motion.div>
                )}
                {phase === "failed" && (
                  <motion.div key="failed" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-10 grid place-items-center text-center" data-testid="checkout-failed">
                    <XCircle className="w-14 h-14 text-red-500" />
                    <div className="mt-3 font-heading font-bold text-xl text-brand-navy">Couldn&#39;t confirm payment</div>
                    <div className="text-xs text-brand-navy/50 mt-1 max-w-xs">{errorMsg || "The payment gateway didn't return a valid confirmation."}<br />No amount will be captured.</div>
                    <div className="mt-5 flex gap-2">
                      <Button onClick={() => setPhase("review")} data-testid="checkout-retry-btn" className="rounded-full bg-brand-red hover:bg-brand-red-hover">Try again</Button>
                      <Button variant="outline" onClick={() => handleOpenChange(false)} className="rounded-full">Close</Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EbookCard = ({ book, index, owned, user, onBuy, onDownload, downloading }) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`ebook-card-${book.id}`}
      className="group rounded-3xl bg-white border border-brand-navy/10 overflow-hidden card-hover flex flex-col"
    >
      <div className="relative overflow-hidden bg-brand-muted-bg">
        <img src={book.coverImage} alt={book.title} loading="lazy" className="w-full aspect-[3/4] object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] font-bold text-brand-red uppercase tracking-wider shadow-sm">
          E-Book · PDF
        </div>
        {owned && (
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold shadow-sm flex items-center gap-1" data-testid={`ebook-owned-badge-${book.id}`}>
            <CheckCircle2 className="w-3 h-3" /> Owned
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-bold text-lg text-brand-navy leading-snug">{book.title}</h3>
        <p className="mt-2 text-sm text-brand-navy/60 leading-relaxed line-clamp-3 flex-1">{book.description}</p>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="font-heading font-black text-2xl text-brand-navy">{inr(book.price)}</div>
        </div>

        {!user ? (
          <Button
            onClick={() => navigate("/login")}
            data-testid={`ebook-login-btn-${book.id}`}
            variant="outline"
            className="mt-4 w-full h-11 rounded-full border-brand-navy/20 text-brand-navy font-semibold hover:bg-brand-navy hover:text-white btn-scale"
          >
            <Lock className="w-4 h-4 mr-1.5" /> Login to Purchase
          </Button>
        ) : owned ? (
          <Button
            onClick={() => onDownload(book)}
            disabled={downloading === book.id}
            data-testid={`ebook-download-btn-${book.id}`}
            className="mt-4 w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold btn-scale"
          >
            {downloading === book.id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Download PDF
          </Button>
        ) : (
          <Button
            onClick={() => onBuy(book)}
            data-testid={`ebook-buy-btn-${book.id}`}
            className="mt-4 w-full h-11 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-bold brand-shadow btn-scale"
          >
            Buy Now — {inr(book.price)}
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export default function EbookStore() {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [purchased, setPurchased] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkout, setCheckout] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const calls = [api.get("/ebooks")];
        if (user) calls.push(api.get("/ebooks/me/purchases"));
        const [b, p] = await Promise.all(calls);
        setBooks(b.data);
        if (p) setPurchased(p.data.purchasedBooks || []);
      } catch (e) {
        toast.error("Could not load the e-book store");
      } finally { setLoading(false); }
    })();
  }, [user]);

  const ownedSet = useMemo(() => new Set(purchased), [purchased]);

  const onDownload = async (book) => {
    setDownloading(book.id);
    try {
      const { data } = await api.get(`/ebooks/${book.id}/download`);
      const a = document.createElement("a");
      a.href = data.pdfFileUrl;
      a.download = `${data.title}.pdf`;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloading "${data.title}"`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    } finally { setDownloading(null); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand-red/15 blur-3xl animate-blob" />
        <div className="absolute top-10 right-0 w-72 h-72 rounded-full bg-brand-gold/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">
              <Sparkles className="w-4 h-4" /> Digital Library
            </div>
            <h1 className="mt-3 font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-brand-navy leading-tight">
              The E-Book <span className="text-brand-red">Bundle.</span>
            </h1>
            <p className="mt-4 text-brand-navy/60 text-base md:text-lg max-w-2xl">
              Expert-written guides from our IELTS trainers and visa counsellors. Buy once, download instantly, keep forever.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading ? (
            <div className="py-24 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>
          ) : books.length === 0 ? (
            <div className="py-24 text-center text-brand-navy/50" data-testid="ebook-empty-state">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No e-books published yet. Check back soon!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="ebook-grid">
              {books.map((b, i) => (
                <EbookCard
                  key={b.id}
                  book={b}
                  index={i}
                  user={user}
                  owned={ownedSet.has(b.id)}
                  onBuy={setCheckout}
                  onDownload={onDownload}
                  downloading={downloading}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <CheckoutModal
        book={checkout}
        onClose={() => setCheckout(null)}
        onSuccess={(id) => setPurchased((prev) => (prev.includes(id) ? prev : [...prev, id]))}
      />

      <Footer />
    </div>
  );
}
